import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Box, Button, Paper, Typography, Collapse, IconButton, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ShiftTable from './ShiftTable';
import { searchByTextOngoing, searchByWarningOngoing, changePageOngoing, changeSortModelOngoing, changeFilterByColumnOngoing } from '../../store/actions/shiftActions';
import FormattedShift from '../../models/FormattedShift';
import { RootState } from '../../store/store';
import { showAlert as showGlobalAlert } from '../../store/actions/alertActions';
import ShiftsService from '../../services/ShiftsService';
import { isDriver } from '../../utils/common';
import LocalStorageService from '../../services/LocalStorageService';

interface Props {
  visibleRows: Array<FormattedShift>,
  totalRowCount: number,
  currentPage: number;
  searchedText: string,
  sortModel: any,
  selectedWarning: string,
  filterByText: any,
  filterByWarning: any,
  filterByCol: any,
  onPageChange: any,
  onSort: any,
  onChangeFilterByColumn: any,
  showAlert: (message: any) => void
}

const OngoingShifts: React.FC<Props> = ({
  visibleRows,
  totalRowCount,
  currentPage,
  searchedText,
  sortModel,
  selectedWarning,
  filterByText,
  filterByWarning,
  filterByCol,
  onPageChange,
  onSort,
  onChangeFilterByColumn,
  showAlert
}) => {
  const [processingWarningIds, setProcessingWarningIds] = useState<Set<number>>(new Set());
  const [activeWarnings, setActiveWarnings] = useState<Array<any>>([]);
  const localStorageService = LocalStorageService.getService();
  const roles = localStorageService.getUserRoles();
  const isUserDriver = isDriver(roles);

  React.useEffect(() => {
    if (isUserDriver) {
      const warnings = visibleRows?.flatMap((shift) =>
        (shift.shiftWarnings?.filter((x) => x.acknowledgedOnUtc === null))
          .map((warning) => ({ ...warning, shiftId: shift.id, createdByUser: warning.createdByUser }))) || [];
      setActiveWarnings(warnings);
    }
  }, [visibleRows, isUserDriver]);

  const handleAcknowledgeWarning = async (shiftId: number, warningId: number) => {
    try {
      setProcessingWarningIds((prev) => new Set(prev).add(warningId));
      await ShiftsService.acknowledgeWarning(shiftId, warningId);
      setActiveWarnings((prev) => prev.filter((warning) => warning.id !== warningId));
      showAlert({ message: 'Warning acknowledged successfully', severity: 'success' });
    } catch (error) {
      // showAlert(formatErrorMessage(JSON.parse(error.response)));
    } finally {
      setProcessingWarningIds((prev) => {
        const next = new Set(prev);
        next.delete(warningId);
        return next;
      });
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <> {
      isUserDriver && activeWarnings.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 4, mb: 2 }}>
          {activeWarnings.map((warning) => (
            <Alert
              key={warning.id}
              severity="warning"
              sx={{
                '& .MuiAlert-message': {
                  flex: 1
                },
                alignItems: 'center',
              }}
              action={(
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() =>
                    handleAcknowledgeWarning(warning.shiftId || 0, warning.id || 0)
                  }
                  disabled={processingWarningIds.has(warning.id || 0)}
                  sx={{
                    minWidth: 40,
                    px: isMobile ? 1 : 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {processingWarningIds.has(warning.id || 0) && (
                    <CircularProgress size={16} color="inherit" />
                  )}
                  {!processingWarningIds.has(warning.id || 0) && isMobile && (
                    <CheckIcon />
                  )}
                  {!processingWarningIds.has(warning.id || 0) && !isMobile && (
                    'Acknowledge'
                  )}
                </Button>
              )}
            >
              {warning.message} - {warning.createdByUser?.firstName} {warning.createdByUser?.lastName}
            </Alert>
          ))}
        </Box>
      )}
      <ShiftTable
        shifts={visibleRows}
        totalRowCount={totalRowCount}
        shiftStatus={2}
        currentPage={currentPage}
        searchedText={searchedText}
        sortModel={sortModel}
        filterByCol={filterByCol}
        selectedWarning={selectedWarning}
        onFilterByText={filterByText}
        onFilterByWarning={filterByWarning}
        onPageChange={onPageChange}
        onSort={onSort}
        onChangeFilterByColumn={onChangeFilterByColumn}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  visibleRows: state.shift?.ongoing?.filteredRows,
  totalRowCount: state.shift?.ongoing?.allRows?.length,
  currentPage: state.shift?.ongoing?.currentPage,
  searchedText: state.shift?.ongoing?.searchedText,
  selectedWarning: state.shift?.ongoing?.filterBy,
  sortModel: state.shift?.ongoing?.sortModel,
  filterByCol: state.shift?.ongoing?.filterByCol,
});

const mapDispatchToProps = (dispatch: any) => ({
  filterByText: (text: string) => dispatch(searchByTextOngoing(text)),
  filterByWarning: (text: string) => dispatch(searchByWarningOngoing(text)),
  onPageChange: (page: any) => dispatch(changePageOngoing(page)),
  onSort: (sortModel: any) => dispatch(changeSortModelOngoing(sortModel)),
  onChangeFilterByColumn: (col: any) => dispatch(changeFilterByColumnOngoing(col)),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OngoingShifts);
